import React, {useMemo} from 'react';
import {Check, X} from 'lucide-react';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import Card from '@/shared/components/Card';
import SectionHeader from '@/shared/components/SectionHeader';
import {useMyIncentives} from '@/modules/ohd/hooks/useMyIncentives';
import type {
    OhdGoodhueIncentivesResponse,
    OhdIncentiveBreakdownRow,
    OhdIncentiveObjective,
} from '@/modules/ohd/types';

interface LegendRow {
    title: string;
    description?: string;
    amount: string;
    /** undefined when we don't have a connection — renders as a dash */
    earned?: boolean;
}

interface BreakdownRow {
    test: string;
    result: string;
    /** null when we don't have data for this row — renders as a dash */
    pointsEarned: number | null;
    pointsAvailable: number | null;
}

const DASH = '—'; // em dash

// Program-structure-only fallback. Used when the OHD endpoint can't be
// reached. The labels are static program metadata; values stay dashed
// so we never display fabricated numbers as if they were real.
const FALLBACK_OBJECTIVES: LegendRow[] = [
    {title: 'Participation', amount: DASH},
    {
        title: 'Biometric Score',
        description: 'Score >= 70 or improved by 5 points from prior year',
        amount: DASH,
    },
];

const FALLBACK_TESTS = [
    'BMI',
    'Waist',
    'Blood Pressure',
    'Cholesterol Ratio',
    'HDL',
    'LDL',
    'Triglycerides',
    'Glucose',
    'Nicotine',
];

const FALLBACK_BREAKDOWN: BreakdownRow[] = FALLBACK_TESTS.map((test) => ({
    test,
    result: DASH,
    pointsEarned: null,
    pointsAvailable: null,
}));

function formatAmount(raw: number | string | undefined): string {
    if (typeof raw === 'string') return raw.startsWith('$') ? raw : `$${raw}`;
    if (typeof raw === 'number' && Number.isFinite(raw)) return `$${raw}`;
    return DASH;
}

function adaptObjectives(apiObjectives: OhdIncentiveObjective[] | undefined): LegendRow[] {
    if (!Array.isArray(apiObjectives) || apiObjectives.length === 0) return FALLBACK_OBJECTIVES;
    return apiObjectives.map((obj) => ({
        title: typeof obj.objective === 'string' ? obj.objective : '',
        description: typeof obj.description === 'string' ? obj.description : undefined,
        amount: formatAmount(obj.amount),
        earned: typeof obj.earned === 'boolean' ? obj.earned : undefined,
    }));
}

function adaptBreakdown(rows: OhdIncentiveBreakdownRow[] | undefined): BreakdownRow[] {
    if (!Array.isArray(rows) || rows.length === 0) return FALLBACK_BREAKDOWN;
    return rows.map((row) => ({
        test: typeof row.test === 'string' ? row.test : '',
        result: row.result == null ? DASH : String(row.result),
        pointsEarned: typeof row.points_earned === 'number' ? row.points_earned : null,
        pointsAvailable: typeof row.points_available === 'number' ? row.points_available : null,
    }));
}

const ObjectivesLegend: React.FC<{rows: LegendRow[]}> = ({rows}) => (
    <div className="overflow-hidden rounded-2xl border border-mld-panel-border bg-white shadow-sm">
        <table className="w-full text-sm">
            <thead className="bg-mld-background/60">
                <tr>
                    <th className="px-4 py-2 text-left font-semibold text-mld-text-primary">Objective</th>
                    <th className="px-4 py-2 text-center font-semibold text-mld-text-primary">Amount</th>
                    <th className="px-4 py-2 text-center font-semibold text-mld-text-primary">Earned</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => (
                    <tr key={i} className="border-t border-mld-panel-border align-top">
                        <td className="max-w-xs px-4 py-2 text-mld-text-primary">
                            <div className="font-medium">{row.title}</div>
                            {row.description && (
                                <div className="mt-1 text-xs text-mld-muted-text">{row.description}</div>
                            )}
                        </td>
                        <td className="px-4 py-2 text-center text-mld-text-primary">{row.amount}</td>
                        <td className="px-4 py-2 text-center">
                            {row.earned === true ? (
                                <span
                                    className="inline-flex h-5 w-5 items-center justify-center rounded bg-mld-primary text-white"
                                    aria-label="Earned">
                                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                                </span>
                            ) : row.earned === false ? (
                                <span
                                    className="inline-flex h-5 w-5 items-center justify-center rounded bg-mld-tertiary text-white"
                                    aria-label="Not earned">
                                    <X className="h-3.5 w-3.5" strokeWidth={3} />
                                </span>
                            ) : (
                                <span className="text-mld-muted-text" aria-label="No data">
                                    {DASH}
                                </span>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const IncentivesPage: React.FC = () => {
    const {data, isLoading, isError, error} = useMyIncentives();

    const status = (error as {status?: number; response?: {status?: number}} | null)?.status
        ?? (error as {response?: {status?: number}} | null)?.response?.status;
    const notLinked = isError && status === 404;
    const upstreamFailed = isError && !notLinked;

    const payload = (data ?? {}) as OhdGoodhueIncentivesResponse;

    const objectivesRows = useMemo(() => adaptObjectives(payload.objectives), [payload.objectives]);
    const breakdownRows = useMemo(
        () => adaptBreakdown(payload.biometric_score?.tests),
        [payload.biometric_score]
    );

    const serverEarned = payload.biometric_score?.total_points_earned;
    const serverAvailable = payload.biometric_score?.total_points_available;
    const totalEarnedDisplay: number | string =
        typeof serverEarned === 'number' ? serverEarned : DASH;
    const totalAvailableDisplay: number | string =
        typeof serverAvailable === 'number' ? serverAvailable : DASH;

    const titleYear = typeof payload.campaign_year === 'number' ? ` ${payload.campaign_year}` : '';

    const showDashedTable = upstreamFailed;

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Custom header layout: legend top-aligned with the title rather than baseline-aligned,
                    so the taller legend doesn't push the page title down. */}
                <div className="my-8 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <h1 className="text-5xl font-light text-mld-text-primary tracking-tight">
                        Incentive Program{titleYear}
                    </h1>
                    <div className="flex shrink-0 items-start">
                        <ObjectivesLegend rows={objectivesRows} />
                    </div>
                </div>

                {upstreamFailed && (
                    <div className="rounded-lg border border-mld-tertiary/30 bg-mld-tertiary/10 px-4 py-3 text-sm text-mld-text-primary">
                        We couldn&apos;t load your incentive data right now. Values will appear here once the connection
                        is restored.
                    </div>
                )}

                <Card variant="elevated" rounded="2xl" className="p-6">
                    <div className="mb-2 text-center">
                        <SectionHeader title="Biometric Score Breakdown" className="!mb-1" />
                        <a
                            href="/goodhue-county-biometric-points.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-mld-primary underline-offset-2 hover:underline">
                            More Info on Biometric Points Calculations (PDF)
                        </a>
                    </div>

                    <div className="mt-6 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-mld-panel-border text-mld-text-primary">
                                    <th className="px-4 py-3 text-left font-semibold">Test</th>
                                    <th className="px-4 py-3 text-left font-semibold">Result</th>
                                    <th className="px-4 py-3 text-left font-semibold">Points Earned</th>
                                    <th className="px-4 py-3 text-left font-semibold">Points Available</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading && !data ? (
                                    <tr>
                                        <td className="px-4 py-6 text-center text-mld-muted-text" colSpan={4}>
                                            Loading your incentive data&hellip;
                                        </td>
                                    </tr>
                                ) : notLinked ? (
                                    <tr>
                                        <td className="px-4 py-6 text-center text-mld-muted-text" colSpan={4}>
                                            We don&apos;t have an OHD profile linked to your account yet. Once your
                                            screening results are in, your incentive breakdown will appear here.
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {breakdownRows.map((row) => (
                                            <tr
                                                key={row.test}
                                                className="border-b border-mld-panel-border/50 last:border-b-0">
                                                <td className="px-4 py-3 text-mld-text-primary">{row.test}</td>
                                                <td className="px-4 py-3 text-mld-text-primary">{row.result}</td>
                                                <td className="px-4 py-3 text-mld-text-primary">
                                                    {row.pointsEarned ?? DASH}
                                                </td>
                                                <td className="px-4 py-3 text-mld-text-primary">
                                                    {row.pointsAvailable ?? DASH}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className="bg-mld-background/50 font-semibold text-mld-text-primary">
                                            <td className="px-4 py-3" colSpan={2}>
                                                Total
                                            </td>
                                            <td className="px-4 py-3">
                                                {showDashedTable ? DASH : totalEarnedDisplay}
                                            </td>
                                            <td className="px-4 py-3">
                                                {showDashedTable ? DASH : totalAvailableDisplay}
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </DashboardLayout>
    );
};

export default IncentivesPage;
